import React, { useState } from 'react';
import {
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifButton,
  MotifInput,
  MotifLabel,
  MotifFormField,
  MotifIcon,
  MotifTextLink,
} from '@ey-xd/motif-react';
import { contentIcAddCircle24px, contentIcCreate24px } from '@ey-xd/motif-react/assets/icons';
import { formateTOV } from '@util/commonUtil';
import {
  Msl_Opportunity_State,
  Active_Tab_Msl,
  All_Month_MSL_Data_State,
  Msl_Opp_View_State,
  Recoil_Msl_Quarter_State,
  System_Date_Time_State,
} from '@recoil/recoil';
import { useRecoilState, useRecoilValue } from 'recoil';
import { MslJudgeModalSpan } from '@atoms/msl/mslTable.styles';
import { MSL_JUDGE_MODAL_CONST } from '@constants/commonConstants';

const MslJudgeModal = ({ KeyName, isVisible, totalCommitted }) => {
  const [value, setValue] = useState('');
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [yourTotalCommit, setYourTotalCommit] = useState(totalCommitted); // eslint-disable-line
  const [isModalVisible, setModalVisibility] = useState(false);
  const [submittedValue, setSubmittedValue] = useState(''); // eslint-disable-line
  const [Your_Total_Commit, setYour_Total_Commit] = useState('TBD');
  const [mslData, setMSLData] = useRecoilState(Msl_Opportunity_State); // eslint-disable-line
  const [mslOppViewData, setMslOppViewData] = useRecoilState(Msl_Opp_View_State); // eslint-disable-line
  const tabActive = useRecoilValue(Active_Tab_Msl);
  const [allMonthData, setAllMonthData] = useRecoilState(All_Month_MSL_Data_State); // eslint-disable-line
  const quarterData = useRecoilValue(Recoil_Msl_Quarter_State);
  const systemDateTime = useRecoilValue(System_Date_Time_State);
  const [isEditMode, setIsEditMode] = useState(false); // eslint-disable-line

  let totalCommitValue = 0;
  const previousTotalCommit = null;
  let yourJudge = 0;
  let yourTotalCommitValue = 0;
  const totalCommittedData = Array.isArray(allMonthData[tabActive])
    ? allMonthData[tabActive].find(
        obj => obj.category === 'Total Commit (PL Commit + Audit/TR + GCSP Judge)',
      )
    : null;
  if (totalCommittedData) {
    totalCommitValue = totalCommittedData[KeyName.toLowerCase()] ?? 0;
  } else {
    totalCommitValue =
      mslData.find(item => item.category === 'Total Commit (PL Commit + Audit/TR + GCSP Judge)')?.[
        KeyName.toLowerCase()
      ] ?? 0;
  }
  const judgeData = Array.isArray(allMonthData[tabActive])
    ? allMonthData[tabActive].find(obj => obj.category === 'Your Judge')
    : null;
  if (judgeData) {
    yourJudge = judgeData[KeyName.toLowerCase()] ?? 0;
  } else {
    yourJudge = mslData.find(item => item.category === 'Your Judge')?.[KeyName.toLowerCase()] ?? 0;
  }

  const yourTotalCommitData = Array.isArray(allMonthData[tabActive])
    ? allMonthData[tabActive].find(obj => obj.category === 'Your Total Commit')
    : null;
  if (yourTotalCommitData) {
    yourTotalCommitValue = yourTotalCommitData[KeyName.toLowerCase()] ?? 0;
  } else {
    yourTotalCommitValue =
      mslData.find(item => item.category === 'Your Total Commit')?.[KeyName.toLowerCase()] ?? 0;
  }

  const TodaysDate = systemDateTime ? new Date(systemDateTime) : new Date();
  const personaStartDateCheck = quarterData ? new Date(quarterData?.persona_startdate) : new Date();
  const personaCloseDateCheck = quarterData ? new Date(quarterData?.persona_closedate) : new Date();
  const isReadOnly = localStorage.getItem('isReadOnly') === 'true';
  let IsSubmitDisabled = false;

  IsSubmitDisabled =
    isReadOnly ||
    !(TodaysDate >= personaStartDateCheck && TodaysDate <= personaCloseDateCheck) ||
    (localStorage.getItem('isSuperUser') == 'true' &&
      localStorage.getItem('targetUserEmail') !== 'null')
      ? true
      : false;

  const handleOpenModal = e => {
    e.preventDefault();
    if (value === '-') {
      setValue('');
    }
    // Validate retained value to set correct save button state
    // This validation must run BEFORE opening the modal to ensure save button state is correct
    // when modal opens with a retained value (moved from inside if (!IsSubmitDisabled) block)
    if (value !== '' && value !== '-') {
      const numericValue = Number(value);
      const newTotalCommit = Number(totalCommitValue) + numericValue;
      setIsSaveDisabled(newTotalCommit < 0);
    } else {
      // Enable save button when input is empty or just '-' since saving 0 is a valid business case
      // Originally, setIsSaveDisabled(false) was inside the if (!IsSubmitDisabled) block at line 113,
      // which unconditionally enabled the save button regardless of retained value validation.
      // Moved here to ensure validation happens first - if retained value would make Total Commit negative,
      // save button stays disabled; otherwise it's enabled for empty input (allowing user to save 0).
      setIsSaveDisabled(false);
    }
    if (!IsSubmitDisabled) {
      setModalVisibility(true);
      setIsEditMode(false);
    }
    setYourTotalCommit(Number(totalCommitValue) + Number(yourJudge));
  };

  const handleSubmit = e => {
    e.preventDefault();
    judgeValueCalculation();
    setModalVisibility(false);
  };

  const handleEdit = e => {
    e.preventDefault();
    if (!IsSubmitDisabled) {
      setModalVisibility(true);
      setIsEditMode(true);
      setValue(yourJudge);
      setYour_Total_Commit(previousTotalCommit || Number(totalCommitValue) + Number(yourJudge));
      // Validate existing value to ensure save button state is correct
      const newTotalCommit = Number(totalCommitValue) + Number(yourJudge);
      setIsSaveDisabled(newTotalCommit < 0);
    }
  };

  const judgeValueCalculation = () => {
    yourJudge = Number(value);
    yourTotalCommitValue = Number(value);
    setMSLData(data =>
      data.map(item => {
        if (item.category === 'Your Judge') {
          const updatedJudgeValue = { ...item, [KeyName.toLowerCase()]: yourJudge };

          const fieldsToSum = ['assurance', 'consulting', 'tax', 'sat', 'itts', 'emeia'];
          const newTotal = fieldsToSum.reduce(
            (acc, field) => acc + (isNaN(updatedJudgeValue[field]) ? 0 : updatedJudgeValue[field]),
            0,
          );
          updatedJudgeValue.total = newTotal;
          return updatedJudgeValue;
        } else if (item.category === 'Your Total Commit') {
          const updatedItem = { ...item, [KeyName.toLowerCase()]: Your_Total_Commit };

          const fieldsToSum = ['assurance', 'consulting', 'tax', 'sat', 'itts', 'emeia'];
          const newTotal = fieldsToSum.reduce(
            (acc, field) => acc + (isNaN(updatedItem[field]) ? 0 : updatedItem[field]),
            0,
          );
          updatedItem.total = newTotal;
          return updatedItem;
        }
        return item;
      }),
    );

    const updatedMonthData = allMonthData[tabActive].map(obj => {
      if (obj.category === 'Your Judge') {
        const fieldsToSum = ['assurance', 'consulting', 'tax', 'sat', 'itts', 'emeia'];
        const newTotal = fieldsToSum.reduce(
          (acc, field) => acc + (isNaN(obj[field]) ? 0 : Number(obj[field])),
          0,
        );
        return { ...obj, [KeyName.toLowerCase()]: yourJudge, total: newTotal };
      }
      if (obj.category === 'Your Total Commit') {
        const fieldsToSum = ['assurance', 'consulting', 'tax', 'sat', 'itts', 'emeia'];
        const newTotal = fieldsToSum.reduce(
          (acc, field) => acc + (isNaN(obj[field]) ? 0 : Number(obj[field])),
          0,
        );
        return {
          ...obj,
          [KeyName.toLowerCase()]: yourTotalCommitValue + totalCommitValue,
          total: newTotal + yourTotalCommitValue,
        };
      }
      return obj;
    });

    setAllMonthData({
      ...allMonthData,
      [tabActive]: updatedMonthData,
    });
  };
  return (
    <>
      {yourJudge ? (
        <MslJudgeModalSpan>
          <MotifTextLink
            variant="alt"
            href="/"
            onClick={handleEdit}
            disabled={IsSubmitDisabled}
            aria-label={`Edit Your Judge ${yourJudge}`}
          >
            <MotifIcon src={contentIcCreate24px} />
            <span aria-hidden="true">{yourJudge ? formateTOV(yourJudge) : ''}</span>
          </MotifTextLink>
        </MslJudgeModalSpan>
      ) : (
        <MotifTextLink variant="alt" href="/" onClick={handleOpenModal} disabled={IsSubmitDisabled}>
          <MotifIcon src={contentIcAddCircle24px} />
          Add Judge
        </MotifTextLink>
      )}
      <MotifModal
        show={isModalVisible || isVisible}
        variant="alt"
        onClose={() => setModalVisibility(false)}
        className="modal-container"
        focusTrapOptions={{
          initialFocus: false,
        }}
      >
        <MotifModalHeader className="modal-header" closeModalButton={true}>
          {KeyName.toLowerCase() === 'assurance'
            ? 'Judge Value: Assurance'
            : KeyName.toLowerCase() === 'consulting'
              ? 'Judge Value: Consulting'
              : KeyName.toLowerCase() === 'sat'
                ? 'Judge Value: SAT'
                : KeyName.toLowerCase() === 'tax'
                  ? 'Judge Value: Tax'
                  : KeyName.toLowerCase() === 'itts'
                    ? 'Judge Value: ITTS'
                    : KeyName.toLowerCase() === 'emeia'
                      ? 'Judge Value: EMEIA ACR'
                      : ''}
        </MotifModalHeader>
        <MotifModalBody className="custom-modal-body">
          <div className="row-layout">
            <div className="table-data1">
              <div className="text-value1">{MSL_JUDGE_MODAL_CONST.PL_GCSP_COMMIT}</div>
            </div>
            <div className="table-data2">
              <div className="text-value2">
                {totalCommitValue ? formateTOV(totalCommitValue) : '$0'}
              </div>
            </div>
          </div>
          <div className="row-layout">
            <div className="table-data1">
              <div className="text-value1">Your Judge</div>
            </div>
            <MotifFormField className="input-area">
              {value === '' ? <MotifLabel className="text-label">Add Judge</MotifLabel> : null}
              <MotifInput
                className="input-value"
                value={value}
                onChange={e => {
                  const inputValue = e.target.value;
                  //A max of 12 digits without the decimal points can be entered by user, including decimals it'd be 15 chars
                  if (/^-?\d{0,12}(\.\d{0,2})?$/.test(inputValue)) {
                    const numericInputValue = inputValue === '-' ? 0 : +inputValue;
                    setValue(inputValue);
                    const newTotalCommit = Number(totalCommitValue) + Number(numericInputValue);
                    const shouldDisableSave = newTotalCommit < 0;
                    setIsSaveDisabled(shouldDisableSave);
                    setYour_Total_Commit(newTotalCommit);
                    setSubmittedValue(numericInputValue);
                  }
                }}
                required={true}
              />
            </MotifFormField>
          </div>
          <div className="row-layout">
            <div className="table-data1">
              <div className="text-value1">Your Total Commit</div>
            </div>
            <div className="table-data2">
              <div className="text-value2">
                {Your_Total_Commit !== 'TBD' ? formateTOV(Your_Total_Commit) : '$0'}
              </div>
            </div>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton type="button" onClick={handleSubmit} disabled={isSaveDisabled}>
            Save
          </MotifButton>
          <MotifButton variant="secondary" onClick={() => setModalVisibility(false)}>
            Cancel
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    </>
  );
};

export default MslJudgeModal;
